"use client";

import { useState, useEffect, useCallback } from "react";

export type Theme = "light" | "dark";

const THEME_STORAGE_KEY = "cq-theme";

// Get initial theme from localStorage or default to dark
function getInitialTheme(): Theme {
  if (typeof window === "undefined") return "dark";
  
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === "light" || stored === "dark") {
    return stored;
  }
  
  // Check system preference
  if (window.matchMedia?.("(prefers-color-scheme: light)").matches) {
    return "light";
  }
  
  return "dark";
}

// Apply theme to document
function applyThemeToDocument(theme: Theme) {
  if (typeof document === "undefined") return;
  
  const root = document.documentElement;
  root.classList.remove("light", "dark");
  root.classList.add(theme);
  
  // Also set color-scheme for native elements
  root.style.colorScheme = theme;
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>("dark");
  const [mounted, setMounted] = useState(false);

  // Initialize theme on mount
  useEffect(() => {
    const initialTheme = getInitialTheme();
    setThemeState(initialTheme);
    applyThemeToDocument(initialTheme);
    setMounted(true);
  }, []);

  // Set theme and persist
  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    applyThemeToDocument(newTheme);
    localStorage.setItem(THEME_STORAGE_KEY, newTheme);
  }, []);

  // Toggle between light and dark
  const toggleTheme = useCallback(() => {
    setTheme(theme === "dark" ? "light" : "dark");
  }, [theme, setTheme]);

  return {
    theme,
    setTheme,
    toggleTheme,
    isDark: theme === "dark",
    isLight: theme === "light",
    mounted, // Use this to avoid hydration mismatch
  };
}

export function useChartColors() {
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() => setGeneration((n) => n + 1));
    observer.observe(root, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  if (typeof window === "undefined") {
    return {
      grid: "#1e293b",
      axis: "#64748b",
      tick: "#94a3b8",
      axisLine: "#475569",
    };
  }

  void generation;
  const styles = getComputedStyle(document.documentElement);
  const read = (name: string, fallback: string) =>
    styles.getPropertyValue(name).trim() || fallback;

  return {
    grid: read("--color-chart-grid", "#1e293b"),
    axis: read("--color-chart-axis", "#64748b"),
    tick: read("--color-chart-tick", "#94a3b8"),
    axisLine: read("--color-border-strong", "#475569"),
  };
}
